/** Shared form switch for Old and New login containers. */
import { ChangeEvent, FC, FormEvent } from 'react';
import { CircularProgress, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { IUserLogin } from '../../../transcepta-types';
import LoginForm from './LoginForm';
import ForgotPasswordForm from './ForgotPasswordForm';
import SSOForm from './SSOForm';
import LogoutForm from './LogoutForm';
import InternalLoginForm from './InternalLoginForm';
import { FormVariant } from './types';

const useStyles = makeStyles(() => ({
    loading: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
    },
}));

export interface ILoginPageContentProps {
    formVariant: FormVariant;
    userLogin: IUserLogin;
    isLoading: boolean;
    email: string;
    password: string;
    companyId: string;
    emailError: boolean;
    passwordError: boolean;
    companyIdError: boolean;
    handleEmailChange: (event: ChangeEvent) => void;
    handlePasswordChange: (event: ChangeEvent) => void;
    handleCompanyIdChange: (companyId: string) => void;
    handleSignIn: (event: FormEvent) => void;
    handleForgotPassword: () => void;
    handleReset: (event: FormEvent) => void;
    handleBackToSignIn: () => void;
    handleSSOSignIn: () => void;
    handleLogInAgain: () => void;
    isBuyer?: boolean;
    useCardFormLayout?: boolean;
}

export const LoginPageContent: FC<ILoginPageContentProps> = ({
    formVariant,
    userLogin,
    isLoading,
    email,
    password,
    companyId,
    emailError,
    passwordError,
    companyIdError,
    handleEmailChange,
    handlePasswordChange,
    handleCompanyIdChange,
    handleSignIn,
    handleForgotPassword,
    handleReset,
    handleBackToSignIn,
    handleSSOSignIn,
    handleLogInAgain,
    isBuyer,
    useCardFormLayout = false, // new login form layout
}) => {
    const classes = useStyles();

    const getDataSelector: Record<string, string> = {
        forgotPassword: 'Resetting Password',
        loggingOut: 'Signing Out',
        default: 'Fetching User Profile',
    };

    if (isLoading) {
        return (
            <div className={classes.loading}>
                <CircularProgress color="secondary" />
                <Typography component="span" variant="caption">
                    {getDataSelector[formVariant] ?? getDataSelector.default}
                </Typography>
            </div>
        );
    }

    switch (formVariant) {
        case 'login':
            return (
                <LoginForm
                    isBuyer={isBuyer}
                    email={email}
                    emailError={emailError}
                    handleEmailChange={handleEmailChange}
                    password={password}
                    passwordError={passwordError}
                    handlePasswordChange={handlePasswordChange}
                    handleSignIn={handleSignIn}
                    handleForgotPassword={handleForgotPassword}
                    useCardLayout={useCardFormLayout}
                />
            );
        case 'forgotPassword':
            return (
                <ForgotPasswordForm
                    email={email}
                    emailError={emailError}
                    handleEmailChange={handleEmailChange}
                    handleReset={handleReset}
                    handleBackToSignIn={handleBackToSignIn}
                    useCardLayout={useCardFormLayout}
                />
            );
        case 'sso':
            return (
                <SSOForm
                    handleSSOSignIn={handleSSOSignIn}
                    handleLogInAgain={handleLogInAgain}
                    userLogin={userLogin}
                    useCardLayout={useCardFormLayout}
                />
            );
        case 'internal':
            return (
                <InternalLoginForm
                    email={email}
                    emailError={emailError}
                    handleEmailChange={handleEmailChange}
                    password={password}
                    passwordError={passwordError}
                    handlePasswordChange={handlePasswordChange}
                    companyId={companyId}
                    companyIdError={companyIdError}
                    handleCompanyIdChange={handleCompanyIdChange}
                    handleSignIn={handleSignIn}
                    handleForgotPassword={handleForgotPassword}
                    isBuyer={isBuyer}
                    useCardLayout={useCardFormLayout}
                />
            );
        case 'logout':
            return <LogoutForm handleLogInAgain={handleLogInAgain} />;
        default:
            return null;
    }
};
